import { useState, useEffect } from "react";
import PlatformLayout from "../components/PlatformLayout";
import { getAgents, createAgent, updateAgent, deleteAgent, type Agent } from "@/utils/platformApi";

export default function PlatformAgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  useEffect(() => {
    fetchAgents();
  }, [statusFilter, searchQuery]);

  const fetchAgents = async () => {
    try {
      const res = await getAgents({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchQuery || undefined,
        limit: 100,
      });
      setAgents(res.list);
    } catch (err) {
      console.error('获取代理商列表失败:', err);
      showToast("获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingAgent(null);
    setShowModal(true);
  };

  const handleEdit = (agent: Agent) => {
    setEditingAgent(agent);
    setShowModal(true);
  };

  const handleSave = async (data: any) => {
    setSubmitting(true);
    try {
      if (editingAgent) {
        await updateAgent(editingAgent.id, data);
        showToast("代理商信息已更新");
      } else {
        await createAgent(data);
        showToast("代理商创建成功");
      }
      setShowModal(false);
      fetchAgents();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (agent: Agent) => {
    try {
      const newStatus = agent.status === "active" ? "suspended" : "active";
      await updateAgent(agent.id, { status: newStatus });
      showToast(newStatus === "active" ? "代理商已启用" : "代理商已停用");
      fetchAgents();
    } catch (err: any) {
      showToast(err.message || "操作失败");
    }
  };

  const handleDelete = async (agent: Agent) => {
    if (!confirm(`确定要删除代理商「${agent.nickname || agent.username}」吗？`)) return;
    try {
      await deleteAgent(agent.id);
      showToast("代理商已删除");
      fetchAgents();
    } catch (err: any) {
      showToast(err.message || "删除失败");
    }
  };

  const filteredAgents = agents;

  const statusMap: Record<string, { label: string; bg: string; color: string }> = {
    active: { label: "正常", bg: "bg-green-50", color: "text-green-600" },
    suspended: { label: "已停用", bg: "bg-red-50", color: "text-red-600" },
    trial: { label: "试用中", bg: "bg-yellow-50", color: "text-yellow-600" },
  };

  return (
    <PlatformLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-64">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索代理商名称或账号..."
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
            >
              <option value="all">全部状态</option>
              <option value="active">正常</option>
              <option value="trial">试用中</option>
              <option value="suspended">已停用</option>
            </select>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" />
            新建代理商
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-800">{agents.length}</div>
            <div className="text-sm text-gray-500">代理商总数</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-green-600">{agents.filter(a => a.status === "active").length}</div>
            <div className="text-sm text-gray-500">正常</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100 text-center">
            <div className="text-2xl font-bold text-yellow-600">{agents.filter(a => a.status === "trial").length}</div>
            <div className="text-sm text-gray-500">试用中</div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-5 py-3 font-medium">代理商</th>
                  <th className="px-5 py-3 font-medium">联系方式</th>
                  <th className="px-5 py-3 font-medium">站点数</th>
                  <th className="px-5 py-3 font-medium">套餐</th>
                  <th className="px-5 py-3 font-medium">到期时间</th>
                  <th className="px-5 py-3 font-medium">状态</th>
                  <th className="px-5 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-gray-400">
                      <i className="ri-loader-4-line animate-spin text-2xl" />
                    </td>
                  </tr>
                ) : filteredAgents.length > 0 ? (
                  filteredAgents.map((agent) => {
                    const status = statusMap[agent.status] || statusMap.trial;
                    return (
                      <tr key={agent.id} className="hover:bg-gray-50">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 font-semibold">
                              {(agent.nickname || agent.username).charAt(0)}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800">{agent.nickname || agent.username}</div>
                              <div className="text-xs text-gray-400">@{agent.username}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">
                          <div>{agent.phone || '-'}</div>
                          <div className="text-xs text-gray-400">{agent.email || '-'}</div>
                        </td>
                        <td className="px-5 py-4 text-sm font-medium text-gray-800">{agent.site_count}</td>
                        <td className="px-5 py-4 text-sm text-gray-600">{agent.plan || '未购买'}</td>
                        <td className="px-5 py-4 text-sm text-gray-500">{agent.expire_at?.split(' ')[0] || '-'}</td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${status.bg} ${status.color}`}>
                            {status.label}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEdit(agent)}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleToggleStatus(agent)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer ${
                                agent.status === "active"
                                  ? "text-red-600 hover:bg-red-50"
                                  : "text-green-600 hover:bg-green-50"
                              }`}
                            >
                              {agent.status === "active" ? "停用" : "启用"}
                            </button>
                            <button
                              onClick={() => handleDelete(agent)}
                              className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                            >
                              删除
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-gray-400">
                      <i className="ri-user-search-line text-3xl mb-2 block" />
                      <p className="text-sm">未找到代理商</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <AgentModal
          agent={editingAgent}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          submitting={submitting}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gray-800 text-white text-sm rounded-xl shadow-lg">
          <i className="ri-checkbox-circle-line text-green-400" />
          {toast}
        </div>
      )}
    </PlatformLayout>
  );
}

function AgentModal({ 
  agent, 
  onClose, 
  onSave,
  submitting 
}: { 
  agent: Agent | null; 
  onClose: () => void; 
  onSave: (data: any) => void;
  submitting: boolean;
}) {
  const [form, setForm] = useState({
    username: agent?.username || "",
    password: "",
    nickname: agent?.nickname || "",
    phone: agent?.phone || "",
    email: agent?.email || "",
  });
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!form.username.trim()) { setError("请输入账号"); return; }
    if (!agent && !form.password.trim()) { setError("请输入密码"); return; }
    if (!form.nickname.trim()) { setError("请输入代理商名称"); return; }

    onSave({
      username: form.username.trim(),
      password: form.password || undefined,
      nickname: form.nickname.trim(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">{agent ? "编辑代理商" : "新建代理商"}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-400" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">账号</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="登录账号"
              disabled={!!agent}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:text-gray-400"
            />
          </div>
          {!agent && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">密码</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="登录密码"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">代理商名称</label>
            <input
              type="text"
              value={form.nickname}
              onChange={(e) => setForm({ ...form, nickname: e.target.value })}
              placeholder="如：云创科技"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">联系电话</label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="手机号码"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">邮箱</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="邮箱地址"
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-xl flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors cursor-pointer"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {submitting ? '处理中...' : (agent ? "保存" : "创建")}
          </button>
        </div>
      </div>
    </div>
  );
}
